/**
 * Currency metadata - specifically the exponent, which is the number of decimal
 * places the currency's minor unit uses.
 *
 * This is why the table exists rather than a hardcoded `* 100` everywhere:
 * most currencies have 2 (INR paise, USD cents), but JPY has 0 (there is no
 * sen any more) and KWD/BHD/OMR have 3 (fils). Rounding a yen amount to two
 * places invents a fraction of a unit that cannot be paid, and rounding a
 * dinar to two places silently loses a third of a fils on every line.
 *
 * Only currencies Fint can actually be configured with are listed. `exponentOf`
 * throws on anything else rather than guessing 2 - a silent wrong exponent is a
 * money bug that surfaces months later in a tax return.
 */
export const CURRENCIES = [
    { code: 'INR', name: 'Indian Rupee', symbol: '₹', exponent: 2, locale: 'en-IN' },
    { code: 'USD', name: 'US Dollar', symbol: '$', exponent: 2, locale: 'en-US' },
    { code: 'EUR', name: 'Euro', symbol: '€', exponent: 2, locale: 'de-DE' },
    { code: 'GBP', name: 'Pound Sterling', symbol: '£', exponent: 2, locale: 'en-GB' },
    { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', exponent: 2, locale: 'en-AE' },
    { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', exponent: 2, locale: 'en-SG' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', exponent: 2, locale: 'en-AU' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', exponent: 2, locale: 'en-CA' },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', exponent: 2, locale: 'de-CH' },
    { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', exponent: 2, locale: 'zh-CN' },
    { code: 'ZAR', name: 'South African Rand', symbol: 'R', exponent: 2, locale: 'en-ZA' },
    { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼', exponent: 2, locale: 'ar-SA' },
    { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM', exponent: 2, locale: 'ms-MY' },
    { code: 'LKR', name: 'Sri Lankan Rupee', symbol: 'Rs', exponent: 2, locale: 'si-LK' },
    // Zero-decimal.
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥', exponent: 0, locale: 'ja-JP' },
    { code: 'KRW', name: 'South Korean Won', symbol: '₩', exponent: 0, locale: 'ko-KR' },
    { code: 'VND', name: 'Vietnamese Dong', symbol: '₫', exponent: 0, locale: 'vi-VN' },
    // Three-decimal.
    { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'د.ك', exponent: 3, locale: 'ar-KW' },
    { code: 'BHD', name: 'Bahraini Dinar', symbol: '.د.ب', exponent: 3, locale: 'ar-BH' },
    { code: 'OMR', name: 'Omani Rial', symbol: '﷼', exponent: 3, locale: 'ar-OM' },
];
const BY_CODE = new Map(CURRENCIES.map((c) => [c.code, c]));
export const DEFAULT_CURRENCY = 'INR';
export function currencyMeta(code) {
    const meta = BY_CODE.get(code.toUpperCase());
    if (!meta) {
        throw new Error(`Unknown currency "${code}". Add it to packages/money/src/currency.ts - ` +
            'guessing an exponent of 2 would silently mis-round every amount in it.');
    }
    return meta;
}
export function exponentOf(code) {
    return currencyMeta(code).exponent;
}
export function isSupportedCurrency(code) {
    return BY_CODE.has(code.toUpperCase());
}
//# sourceMappingURL=currency.js.map